import { QueryClient } from '@tanstack/react-query';
import { getConfigByKey as GetConfigByKey } from '@tn/configs';
import type useFullUserType from '@tn/foundation/hooks/providers/useFullUser';
import { cacheKey as USER_PROVIDER_CACHE_KEY } from '@tn/foundation/hooks/providers/useFullUser';
import { getCanonicalRoute, getRouteWithRegion } from '@tn/foundation/hooks/routes/useRegionRoute';
import { SUBSCRIBER_STATUSES } from '@tn/foundation/utils/constants/userStatus';
import { navigateRegionAwareRoute } from '@tn/foundation/utils/navigateRegionAwareRoute';
import { getMarketingRegionIfEnabled } from '@tn/foundation/utils/server/region-utils';
import type { NextRouter } from 'next/router';

type RouterNavigate = NextRouter['push'];
type RouterUrl = Parameters<RouterNavigate>[0];
type RouterAs = Parameters<RouterNavigate>[1];

/** True when the second `push`/`replace` argument is an explicit `as` UrlObject (not transition options). */
const isRouterUrlObject = (value: unknown): value is Extract<RouterAs, object> =>
    typeof value === 'object' &&
    value !== null &&
    'pathname' in value &&
    typeof (value as { pathname?: unknown }).pathname === 'string';

const isExplicitRouterAs = (value: unknown): value is Extract<RouterAs, string | object> =>
    typeof value === 'string' || isRouterUrlObject(value);

const isSubscriberFromQueryClient = (queryClient: QueryClient): boolean => {
    const user = queryClient.getQueryData<ReturnType<typeof useFullUserType>>([
        USER_PROVIDER_CACHE_KEY,
    ]);

    return (
        !!user?.userFullProfile?.userStatus?.description &&
        SUBSCRIBER_STATUSES.includes(user.userFullProfile.userStatus.description)
    );
};

/**
 * Wraps a Next.js router so `push` / `replace` apply the canonical `href` / regional `as` split
 * expected by Pluto's region-prefixed URLs. Used at flow composition time so every xstate entity
 * gets region-aware SPA navigation without per-entity boilerplate.
 *
 * Subscriber state is read from {@link queryClient} on each navigation so P+ subscriber destinations
 * (e.g. DTC `/home`) stay region-less per {@link getRouteWithRegion}.
 *
 * Callers that already pass an explicit `as` (second string or UrlObject argument) are left unchanged.
 */
export const createRegionAwareFlowRouter = (
    router: NextRouter,
    region: string,
    getConfigByKey: ReturnType<typeof GetConfigByKey>,
    queryClient: QueryClient,
): NextRouter => {
    const allowMarketingRegionInPath = !!getConfigByKey('options.allowMarketingRegionInPath');
    const alwaysIncludeRegion = !!getConfigByKey('options.alwaysIncludeRegion');
    const includeRegionForNonSubscriber = !!getConfigByKey('options.includeRegionForNonSubscriber');
    const regionInPath = getMarketingRegionIfEnabled(allowMarketingRegionInPath, region);
    const canonicalize = getCanonicalRoute({ duplicateRegionSegments: [regionInPath, region] });
    const regionalize = (href: string) =>
        getRouteWithRegion({
            includeRegionForNonSubscriber,
            alwaysIncludeRegion,
            isSubscriber: isSubscriberFromQueryClient(queryClient),
            baseUrl: '',
            region: regionInPath,
            duplicateRegionSegments: [regionInPath, region],
        })(href);

    const wrapNavigate =
        (navigate: RouterNavigate): RouterNavigate =>
        (url, asOrOptions, maybeOptions) => {
            if (isExplicitRouterAs(asOrOptions)) {
                return maybeOptions !== undefined
                    ? navigate(url as RouterUrl, asOrOptions, maybeOptions)
                    : navigate(url as RouterUrl, asOrOptions);
            }

            if (typeof url !== 'string') {
                return navigate(url, asOrOptions, maybeOptions);
            }

            const options =
                typeof asOrOptions === 'object' && asOrOptions !== null
                    ? asOrOptions
                    : maybeOptions;

            return navigateRegionAwareRoute({
                navigate,
                displayUrl: url,
                getCanonicalRoute: canonicalize,
                getRouteWithRegion: regionalize,
                options,
                shallowRoute: options?.shallow
                    ? { pathname: router.pathname, query: router.query }
                    : undefined,
            });
        };

    // Proxy keeps pathname/query/asPath live — a shallow spread freezes those values at
    // machine composition time, breaking in-flow actions (e.g. activateDevice route rewrites).
    const wrappedPush = wrapNavigate(router.push.bind(router));
    const wrappedReplace = wrapNavigate(router.replace.bind(router));

    return new Proxy(router, {
        get(target, prop, receiver) {
            if (prop === 'push') {
                return wrappedPush;
            }
            if (prop === 'replace') {
                return wrappedReplace;
            }
            return Reflect.get(target, prop, receiver);
        },
    }) as NextRouter;
};
